import { Button, Card, CardContent, CardHeader, DrawerRoot, DrawerTrigger, Table, TableColumn } from '@ibsys/kit';
import { format } from 'date-fns';
import { PencilIcon, PlusIcon } from 'lucide-react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ConversationListItem,
  PERSON_ROLES,
  useConversations,
  useCreateConversation,
  useUploadConversationAudio,
} from '@/entities/conversations';
import { useHasPermission } from '@/entities/profile';
import { FileUploadDialog, PageContainer } from '@/shared/ui';
import RecordsFilters from './RecordsFilters';
import { RecordsForm } from './RecordsForm';
import { useTableState } from '@/entities/settings';
import { usePaginatedQuery } from '@/shared/hooks';

export const RecordsPage: FC = () => {
  const { t } = useTranslation(['records']);

  const { settings, actions } = useTableState('recordsList');

  const allowAdmin = useHasPermission('ADMIN_CONVERSATION');

  const {
    data: { conversations = [], total } = {},
    page,
    changePagination,
    perPage,
  } = usePaginatedQuery(useConversations, 'conversations', { filters: settings.filters, sorting: settings.sorting });

  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const { mutateAsync: uploadFile } = useUploadConversationAudio();
  const { mutateAsync: createConversation } = useCreateConversation();

  const handleFileUpload = async (files: File[]) => {
    try {
      setIsUploading(true);

      for (const file of files) {
        const uploadResponse = await uploadFile(file);

        const now = new Date();
        const startedAt = new Date(now);
        startedAt.setHours(13, 0, 0, 0);
        const finishedAt = new Date(startedAt);
        finishedAt.setHours(14, 0, 0, 0);

        await createConversation({
          startedAt: startedAt.toISOString(),
          finishedAt: finishedAt.toISOString(),
          duration: '01:00:00',
          fileName: uploadResponse.fileName,
          properties: {},
          person: [
            {
              role: PERSON_ROLES.EMPLOYEE,
              firstName: 'Иван',
              lastName: 'Петров',
              middleName: 'Сергеевич',
            },
            {
              role: PERSON_ROLES.CLIENT,
              firstName: 'Мария',
              lastName: 'Иванова',
              middleName: 'Ивановна',
            },
          ],
        });
      }

      setIsUploading(false);
      setShowFileUpload(false);
    } catch (error) {
      console.error('Error uploading file or creating conversation:', error);
      setIsUploading(false);
    }
  };

  const columns = [
    {
      accessorKey: 'id',
      header: t('table.id'),
      maxSize: 40,
    },
    {
      accessorKey: 'createdAt',
      header: t('table.callTime'),
      enableSorting: true,
      minSize: 100,
      cell: ({ row }) => format(new Date(row.original.createdAt), 'dd.MM.yyyy HH:mm'),
    },
    {
      accessorKey: 'employee',
      header: t('table.operatorName'),
      enableSorting: true,
      cell: ({ row }) => row.original.employee?.[0]?.fio,
    },
  ] satisfies TableColumn<ConversationListItem>[];

  return (
    <DrawerRoot open={!!openDrawer} onOpenChange={setOpenDrawer}>
      <PageContainer className="h-full w-full">
        <div className={'grid grid-rows-[auto_auto] grid-cols-1 w-full h-full'}>
          <RecordsFilters setActiveFilters={actions.onFiltersChange} filters={settings.filters} className="mb-4" />

          <Card className={'gap-0 flex flex-col'}>
            {allowAdmin && (
              <CardHeader className={'flex gap-2'}>
                <Button variant={'secondary'}>{t('records:actions.runAssessment')}</Button>
                <Button icon={<PlusIcon />} variant={'outline'} onClick={() => setShowFileUpload(true)}>
                  {t('records:actions.add')}
                </Button>
                {selectedRowIds.length > 1 && (
                  <DrawerTrigger asChild>
                    <Button icon={<PencilIcon />} variant={'outline'}>
                      {t('records:actions.edit')}
                    </Button>
                  </DrawerTrigger>
                )}
              </CardHeader>
            )}

            <CardContent className={'p-0 w-full min-w-0'}>
              <Table
                enableColumnOrdering
                tableStateStoragePath={'records'}
                className={'border-0 h-140 max-h-[calc(100dvh_-_120px)] md:max-h-180'}
                columns={columns}
                data={conversations}
                // selectedKeys={selectedRowIds}
                onSelect={({ selectedRows }) => {
                  setSelectedRowIds(selectedRows.map((row) => row.id));
                }}
                // enableMultiSelect={allowAdmin}
                pagination={{
                  total: total || 0,
                  page,
                  perPage,
                }}
                {...settings}
                {...actions}
                type={'paginated'}
                onChangePagination={changePagination}
              />
            </CardContent>
          </Card>
          <RecordsForm
            open={openDrawer}
            onClose={() => {
              setOpenDrawer(false);
              setSelectedRowIds([]);
            }}
            selectedRowIds={selectedRowIds}
          />

          <FileUploadDialog
            isOpen={showFileUpload}
            onClose={() => {
              if (!isUploading) {
                setShowFileUpload(false);
              }
            }}
            onFileUpload={handleFileUpload}
            acceptedFileTypes={['.mp3', '.wav', '.zip']}
            maxFileSize={1000}
            isLoading={isUploading}
          />
        </div>
      </PageContainer>
    </DrawerRoot>
  );
};
